import Transition from "@shared/core/Transition";
import { useEffect, useRef, useState } from "react";
import { QuestionCategory } from "../types/question-bank.types";

interface CategoryDropdownProps {
  align?: "left" | "right";
  categories: QuestionCategory[];
  selectedCategoryId: string | null;
  onChange: (categoryId: string) => void;
  onClose?: () => void;
  placeholder?: string;
}

export default function CategoryDropdown({
  align = "left",
  categories,
  selectedCategoryId,
  onChange,
  onClose,
  placeholder = "Select Category",
}: CategoryDropdownProps) {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set()
  );
  const trigger = useRef<HTMLButtonElement>(null);
  const dropdown = useRef<HTMLDivElement>(null);

  // Find selected category
  const findCategoryById = (
    id: string | null,
    cats: QuestionCategory[]
  ): QuestionCategory | null => {
    if (!id) return null;

    for (const cat of cats) {
      if (cat.id === id) return cat;

      if (cat.subcategories && cat.subcategories.length > 0) {
        const found = findCategoryById(id, cat.subcategories);
        if (found) return found;
      }
    }

    return null;
  };

  const selectedCategory = findCategoryById(selectedCategoryId, categories);

  // Close on click outside
  useEffect(() => {
    const clickHandler = ({ target }: MouseEvent) => {
      if (!dropdown.current) return;
      if (
        dropdown.current.contains(target as Node) ||
        trigger.current?.contains(target as Node)
      )
        return;
      setDropdownOpen(false);
      onClose?.();
    };
    document.addEventListener("click", clickHandler);
    return () => document.removeEventListener("click", clickHandler);
  }, [onClose]);

  // Close if the esc key is pressed
  useEffect(() => {
    const keyHandler = ({ keyCode }: KeyboardEvent) => {
      if (!dropdownOpen || keyCode !== 27) return;
      setDropdownOpen(false);
      onClose?.();
    };
    document.addEventListener("keydown", keyHandler);
    return () => document.removeEventListener("keydown", keyHandler);
  }, [dropdownOpen, onClose]);

  const handleSelect = (categoryId: string, hasSubcategories: boolean) => {
    // Only allow selection of categories without subcategories
    if (!hasSubcategories) {
      onChange(categoryId);
      setDropdownOpen(false);
    }
  };

  const toggleExpand = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Render category item with its subcategories
  const renderCategoryItem = (category: QuestionCategory, level = 0) => {
    const isSelected = category.id === selectedCategoryId;
    const hasSubcategories =
      category.subcategories && category.subcategories.length > 0;
    const isExpanded = expandedCategories.has(category.id);

    return (
      <li key={category.id} className="relative">
        <button
          className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${
            level > 0 ? "pl-" + (level * 4 + 3) : "pl-3"
          } ${
            isSelected
              ? "bg-blue-50 text-blue-600 font-medium"
              : "text-gray-700 hover:bg-gray-50"
          } ${hasSubcategories ? "cursor-pointer" : ""}`}
          onClick={() =>
            hasSubcategories
              ? toggleExpand(category.id)
              : handleSelect(category.id, false)
          }
        >
          <span>{category.name}</span>
          {hasSubcategories && (
            <svg
              className={`h-4 w-4 text-gray-400 transition-transform ${
                isExpanded ? "rotate-90" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          )}
        </button>

        {/* Render subcategories if this category is expanded */}
        {hasSubcategories && isExpanded && (
          <ul className="pl-4">
            {category.subcategories.map((subcat) =>
              renderCategoryItem(subcat, level + 1)
            )}
          </ul>
        )}
      </li>
    );
  };

  // Flatten categories for the main dropdown
  const renderAllCategories = (cats: QuestionCategory[], level = 0) => {
    let result: React.ReactNode[] = [];

    cats.forEach((cat) => {
      result.push(renderCategoryItem(cat, level));
    });

    return result;
  };

  return (
    <div className="relative inline-flex">
      <button
        ref={trigger}
        className="btn bg-white dark:bg-gray-700 border-slate-200 dark:border-slate-700 hover:border-slate-300 text-slate-500 hover:text-slate-600 flex items-center"
        aria-haspopup="true"
        onClick={() => setDropdownOpen(!dropdownOpen)}
        aria-expanded={dropdownOpen}
      >
        <span className="truncate max-w-[150px]">
          {selectedCategory ? selectedCategory.name : placeholder}
        </span>
        <svg className="w-4 h-4 ml-2 fill-current" viewBox="0 0 16 16">
          <path d="M8 11.4l-4.7-4.7 1.4-1.4 3.3 3.3 3.3-3.3 1.4 1.4z" />
        </svg>
      </button>
      <Transition
        show={dropdownOpen}
        tag="div"
        className={`origin-top-right z-10 absolute top-full min-w-[250px] bg-white border border-slate-200 pt-1.5 rounded shadow-lg overflow-hidden mt-1 ${
          align === "right" ? "right-0" : "left-0"
        }`}
        enter="transition ease-out duration-200 transform"
        enterStart="opacity-0 -translate-y-2"
        enterEnd="opacity-100 translate-y-0"
        leave="transition ease-out duration-200"
        leaveStart="opacity-100"
        leaveEnd="opacity-0"
        unmountOnExit={true}
      >
        <div ref={dropdown} className="max-h-[300px] overflow-y-auto">
          <div className="text-xs font-semibold text-slate-400 uppercase pt-1.5 pb-2 px-4">
            Categories
          </div>
          <ul className="mb-4">{renderAllCategories(categories)}</ul>
        </div>
      </Transition>
    </div>
  );
}
